import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Banknote, FileText, Briefcase, CreditCard, Building2, Truck, Zap } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const products = [
    { icon: Banknote, title: "Merchant Cash Advance", amount: "Up to $3M", terms: "Up to 35 weeks", desc: "Revenue-based capital tied to future receivables. Best for businesses with strong daily card volume or healthy bank deposits." },
    { icon: Briefcase, title: "Working Capital", amount: "$10K – $1M", terms: "3 – 24 months", desc: "Fixed-payment short-term capital for inventory, payroll, marketing, and growth. Same-day decisions on complete files." },
    { icon: FileText, title: "Term Loans", amount: "$50K – $2M", terms: "12 – 60 months", desc: "Predictable monthly payments for major investments — equipment, expansion, or refinancing existing high-cost debt." },
    { icon: CreditCard, title: "Business Line of Credit", amount: "Up to $500K", terms: "Revolving", desc: "Draw what you need, pay for what you use. Pre-approved capital that's there when opportunities arise." },
    { icon: Building2, title: "SBA Loans", amount: "Up to $5M", terms: "Up to 25 years", desc: "Government-backed long-term capital with the lowest rates available. We package and broker — banks fund." },
    { icon: Truck, title: "Equipment Financing", amount: "Up to $2M", terms: "Up to 84 months", desc: "Finance trucks, machinery, kitchen equipment, medical devices. The equipment secures the loan — your cash stays in the business." },
    { icon: Zap, title: "Bridge Loans", amount: "Up to $1M", terms: "3 – 24 months", desc: "Short-term capital to cover gaps while you wait on long-term funding, real-estate close, or seasonal swings." },
];

const programs = [
    { v: "C & D Paper", l: "Underwriting flexibility for complex credit profiles" },
    { v: "2nd – 8th Positions", l: "Stack-friendly capital with realistic position math" },
    { v: "$250K – $3M Deals", l: "Built for serious files with serious revenue" },
    { v: "Up to 35-Week Terms", l: "Longer terms keep cash flow healthy" },
    { v: "$50K+ Monthly Revenue", l: "Standard floor; higher unlocks better pricing" },
    { v: "High-Dollar Specialty", l: "We sharpen our pencils on $250K+ requests" },
];

export default function FundingSolutions() {
    usePageTitle("Funding Solutions", "Seven funding programs: MCA, Working Capital, Term Loans, Lines of Credit, SBA, Equipment Financing, Bridge Loans. Up to $3M.");
    return (
        <>
            <section className="bg-white relative overflow-hidden" data-testid="funding-hero">
                <div className="absolute inset-0 bg-grid pointer-events-none" />
                <div className="container-lended relative pt-20 pb-12 md:pt-28 max-w-4xl">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="divider-orange" />
                        <span className="eyebrow">Funding Solutions</span>
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.04em] leading-[0.95]">
                        Capital, structured the way <span className="text-[#FF6B1A]">your business actually runs.</span>
                    </h1>
                    <p className="mt-7 text-lg md:text-xl text-black/70 max-w-3xl">
                        Seven programs. One direct funder. Pick what fits — or call us and we'll build a custom structure
                        for your file.
                    </p>
                </div>
            </section>

            <section className="bg-white py-12 md:py-20" data-testid="products-grid">
                <div className="container-lended">
                    <div className="grid md:grid-cols-2 gap-px bg-black/10">
                        {products.map((p) => (
                            <div key={p.title} className="bg-white p-8 md:p-10 hover:bg-[#F8F5F2] transition-colors" data-testid={`product-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                <div className="flex items-start justify-between gap-6">
                                    <p.icon className="text-[#FF6B1A]" size={32} />
                                    <div className="text-right">
                                        <div className="text-xs uppercase tracking-[0.18em] text-black/50">Amount</div>
                                        <div className="font-display font-bold text-lg">{p.amount}</div>
                                    </div>
                                </div>
                                <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-5">{p.title}</h3>
                                <div className="text-sm text-[#FF6B1A] font-mono mt-1">Terms: {p.terms}</div>
                                <p className="text-black/65 mt-4 leading-relaxed">{p.desc}</p>
                                <Link to="/#apply" className="inline-flex items-center gap-2 mt-6 text-sm font-bold text-black hover:text-[#FF6B1A]">
                                    Get this program <ArrowRight size={16} />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#0A0A0A] text-white py-20 md:py-28 relative overflow-hidden" id="programs" data-testid="active-programs">
                <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
                <div className="container-lended relative">
                    <div className="max-w-2xl mb-14">
                        <span className="eyebrow text-[#FF6B1A]">Active Programs</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1]">
                            Built for the deals that move serious volume.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-px bg-white/10">
                        {programs.map((p) => (
                            <div key={p.v} className="bg-[#0A0A0A] p-8" data-testid={`active-prog-${p.v.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                <div className="font-display text-3xl font-bold text-[#FF6B1A]">{p.v}</div>
                                <p className="text-white/65 mt-3 leading-relaxed">{p.l}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white py-16" data-testid="funding-cta">
                <div className="container-lended flex flex-col md:flex-row gap-6 items-center justify-between">
                    <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight max-w-xl leading-[1]">
                        Not sure which program fits? <span className="text-[#FF6B1A]">We'll figure it out.</span>
                    </h3>
                    <Link to="/#apply" className="btn-orange">Get a Quote <ArrowRight size={18} /></Link>
                </div>
            </section>
        </>
    );
}
